import type { CategoryContent } from './row.js';

export const PORTUGUESE_HISTORY: CategoryContent = {
  bscba: [
    [
      'When was the Kingdom of Portugal founded as an independent Christian state?',
      ['1143', '1139', '1297', '1385'],
      0,
      'Portugal became independent in 1143 under Afonso Henriques. The Treaty of Zamora (1297) later formalized recognition by Castile.',
    ],
    [
      'Who was the first King of an independent Portugal?',
      ['Afonso I Henriques', 'Ferdinand I of Castile', 'Diniz I', 'John I of Avis'],
      0,
      'Afonso I Henriques (r. 1143–1185) founded the Portuguese monarchy. John I came later, in 1385, establishing the House of Avis.',
    ],
    [
      'What was the Reconquista?',
      ['A Christian reconquest of Muslim Iberia', 'An Islamic invasion from North Africa', 'A naval war against the Ottoman Empire', 'A popular revolt against the Inquisition'],
      0,
      'The Reconquista (8th–15th centuries) was Christian kingdoms gradually reconquering the Iberian Peninsula from Muslim rulers. It culminated with Granada\'s fall in 1492.',
    ],
    [
      'Which century saw Portuguese explorers lead the Age of Discovery?',
      ['15th century', '13th century', '14th century', '16th century'],
      0,
      'The Age of Discovery began in the 15th century, driven by patrons like Henry the Navigator. Portuguese explorers mapped coasts from Africa to Asia.',
    ],
    [
      'What was the significance of Vasco da Gama\'s 1498 voyage?',
      ['A direct sea route to Asia', 'He conquered India for Portugal', 'He discovered the Americas', 'He proved the Earth was round'],
      0,
      'Da Gama\'s voyage created the Cape Route, enabling direct maritime trade between Europe and Asia. This bypassed Ottoman-controlled overland routes.',
    ],
    [
      'During the Iberian Union (1580–1640), what was the relationship between Portugal and Spain?',
      ['Same monarch, separate kingdoms', 'Portugal became a Spanish province', 'They merged into a single nation', 'Spain withdrew from Portuguese territory'],
      0,
      'The Iberian Union meant Portugal and Spain had the same monarchs, but Portugal kept its separate legal systems, nobility, and institutional structures.',
    ],
    [
      'How long did the Iberian Union last?',
      ['60 years', '40 years', '80 years', '100 years'],
      0,
      'The Iberian Union lasted 60 years (1580–1640). It ended when John IV declared Portuguese independence and founded the House of Braganza.',
    ],
    [
      'Who led Portugal\'s restoration of independence in 1640?',
      ['John IV', 'Afonso III', 'Manuel I', 'Henry the Navigator'],
      0,
      'John IV of Braganza led the Portuguese Restoration (Restauração) in 1640, overthrowing Spanish rule and founding a dynasty that reigned until 1910.',
    ],
    [
      'Which Portuguese territory declared independence in 1822?',
      ['Brazil', 'Goa', 'Macau', 'East Timor'],
      0,
      'Brazil declared independence in 1822 as the Empire of Brazil. It had been Portugal\'s largest and most profitable colony.',
    ],
    [
      'When was Portugal\'s First Republic established?',
      ['1910', '1865', '1895', '1920'],
      0,
      'The First Republic began in 1910 after a revolution deposed King Manuel II, the last constitutional monarch of the House of Braganza.',
    ],
    [
      'What was the Estado Novo?',
      ['An authoritarian dictatorship', 'A democratic reform movement', 'An economic union with Spain', 'A colonial independence campaign'],
      0,
      'The Estado Novo ("New State") was an authoritarian regime that ruled Portugal from 1926 to 1974, born from a military coup.',
    ],
    [
      'Who was the primary dictator of Portugal\'s Estado Novo?',
      ['Salazar', 'Getúlio Vargas', 'Miguel Primo de Rivera', 'Francisco Franco'],
      0,
      'António de Oliveira Salazar ruled Portugal from 1932 to 1968. His Estado Novo was one of Europe\'s longest-lasting dictatorships.',
    ],
    [
      'In what year did the Carnation Revolution occur?',
      ['1974', '1964', '1984', '1952'],
      0,
      'The Carnation Revolution occurred on April 25, 1974, ending 48 years of dictatorship and establishing democracy.',
    ],
    [
      'Why is the 1974 revolution called the "Carnation Revolution"?',
      ['Carnations were placed in soldiers\' rifles', 'Carnations were the official national flower', 'It happened during spring', 'A revolutionary leader\'s name was Carnation'],
      0,
      'Lisbon civilians spontaneously placed red carnations in soldiers\' rifles and wore them. The flower became an iconic symbol of the peaceful revolution.',
    ],
    [
      'What form of government did Portugal adopt after the Carnation Revolution?',
      ['A parliamentary republic', 'A constitutional monarchy', 'A socialist state', 'A military junta'],
      0,
      'Portugal became a democratic republic with separation of powers, a parliamentary system, and later joined the European Union in 1986.',
    ],
  ],
  msc: [
    [
      'The historiographic debate over the founding of Portugal centers on whether the Battle of Ourique (1139) was:',
      ['A later legendary invention by chroniclers', 'The only significant military event in Portuguese history', 'Proven by contemporary Islamic sources', 'Irrelevant to Portuguese independence'],
      0,
      'Modern scholars debate Ourique\'s historicity; some argue it was a literary invention. The Treaty of Zamora (1297) provides firmer evidence of formal recognition.',
    ],
    [
      'Which economic factor most enabled Portuguese Age of Discovery expeditions?',
      ['Madeira and Azores sugar profits', 'Gold from West African trade alone', 'Spice monopolies from Southeast Asia', 'Wool exports to Flanders'],
      0,
      'Sugar plantations in Atlantic islands generated capital for exploration. These islands later became models for Caribbean slave plantations.',
    ],
    [
      'What did "Estado da Índia" (State of India) refer to?',
      ['Forts and trading posts, not conquest', 'A proposed federation of Asian kingdoms', 'The territory surrounding Goa alone', 'The first European trading post in India'],
      0,
      'The "State of India" was a decentralized network of forts (Goa, Cochin, Diu) and trading posts. Controlling sea routes mattered more than territorial control.',
    ],
    [
      'Philip II of Spain\'s 1580 claim to the Portuguese throne was based on the death of which heir?',
      ['Cardinal Henry, last male Avis heir', 'John III with no legitimate successor', 'King Sebastian\'s uncle', 'Manuel I\'s descendant'],
      0,
      'Cardinal Henry (1578–1580) was the last Avis king with direct male heirs. Philip II, through his mother, claimed the throne, precipitating the Iberian Union.',
    ],
    [
      'Why did Portuguese nobility support the 1640 Restoration despite Spanish military risk?',
      ['Tax hikes and exclusion from office', 'Portugal suddenly discovered wealth in Brazil', 'A popular uprising forced the nobility\'s hand', 'France guaranteed military support'],
      0,
      'Castilian tax increases (especially the tinta sales tax) and exclusion from Spanish office bred resentment. Spain\'s involvement in the Thirty Years\' War limited its capacity to respond.',
    ],
    [
      'What did the Treaty of Lisbon (1668) accomplish?',
      ['Spain recognized Portuguese sovereignty', 'Portugal gained control of Spanish colonies', 'It created a defensive alliance against France', 'It restored Portuguese control of India'],
      0,
      'Spain formally recognized Portuguese independence in 1668, ending decades of military conflict and international uncertainty over the Braganza dynasty\'s legitimacy.',
    ],
    [
      'The 1826–1834 civil conflict in Portugal pitted which two factions against each other?',
      ['Liberals against absolutists', 'Republicans versus monarchists', 'Rich merchants against poor peasants', 'Portugal against Spain'],
      0,
      'Liberal constitutionalists fought absolutists over governance and church property. British intervention supported the constitutional side, shaping modern Portuguese politics.',
    ],
    [
      'What precipitated the 1910 republican revolution that overthrew the monarchy?',
      ['The assassination of King Carlos I and his heir', 'Economic collapse from colonial losses', 'Military mutiny over wages', 'Pressure from the newly proclaimed Spanish Republic'],
      0,
      'King Carlos and his eldest son were assassinated on February 1, 1910. The succeeding king lasted only two years before the Republic was proclaimed.',
    ],
    [
      'What structural problem made the First Republic (1910–1926) politically unstable?',
      ['Fragmentation and a weak presidency', 'It was too democratic', 'The Senate had too much power', 'Economic prosperity caused complacency'],
      0,
      'Proportional representation created 16 governments in 16 years. A weak presidency and unresolved social divisions left the republic vulnerable to military intervention.',
    ],
    [
      'What ideological tradition most influenced Salazar\'s corporatist Estado Novo?',
      ['Catholic integralism', 'Pure Marxism', 'Liberal democracy', 'Islamic political theory'],
      0,
      'Salazar, a Catholic integralist, grounded the Estado Novo in Church teachings on social hierarchy. Unlike fascism, Portuguese corporatism lacked racial biologism.',
    ],
    [
      'During World War II, Portugal\'s supply of wolfram (tungsten) to Germany served what strategic purpose?',
      ['Leverage against both Axis and Allies', 'It was merely a commercial transaction', 'It signaled fascist allegiance', 'Portugal had no choice'],
      0,
      'Wolfram was critical for tank armor. Portugal\'s supply enabled Salazar to play both sides, funding the Estado Novo while maintaining the fiction of neutrality.',
    ],
    [
      'The secret police under Salazar, the PIDE-DGS, operated primarily through:',
      ['Informant networks', 'Mass arrests and terror', 'Advanced surveillance technology', 'Military patrols and checkpoints'],
      0,
      'PIDE relied on denunciation networks rather than centralized surveillance. This made it episodically brutal but organizationally less efficient than Soviet or Nazi secret police.',
    ],
    [
      'How did the Colonial Wars (1961–1974) destabilize the Estado Novo?',
      ['Costly wars fueled officer unrest', 'The wars were won decisively', 'Colonial forces immediately defected', 'The UN intervened militarily'],
      0,
      'Wars in Angola, Guinea-Bissau, and Mozambique drained the budget and conscripted younger officers who questioned the regime\'s viability. Military defeat fueled the 1974 coup.',
    ],
    [
      'Why did the Armed Forces Movement (MFA) coup succeed with minimal bloodshed in 1974?',
      ['Troops refused to fire on civilians', 'The army mutinied entirely', 'The government voluntarily surrendered', 'International pressure forced compliance'],
      0,
      'The regime lost coercive capacity when conscripts fraternized with civilians rather than firing. Officer divisions prevented the regime from using force to suppress the coup.',
    ],
    [
      'The Portuguese Constitution of 1976 established what form of government?',
      ['A semi-presidential system', 'Pure parliamentary democracy', 'A federalist structure with autonomous regions', 'Military control through an institutional council'],
      0,
      'The 1976 Constitution created a hybrid system combining a powerful directly elected president with parliamentary oversight. This reflected post-1974 political compromise.',
    ],
  ],
  phd: [
    [
      'Fernanda Fonseca and other revisionist scholars argue that Portuguese national consciousness emerged not from 1143 but from:',
      ['Gradual institutional divergence', 'The Battle of Ourique alone', 'Complete separation from Leonese law', 'Royal marriage alliances'],
      0,
      'Modern historiography emphasizes institutional gradualness: church exemptions, legal autonomy, and administrative separation preceded military claims to independence.',
    ],
    [
      'The historiographic debate between "commercial network" vs. "imperial extractive" framings of the Estado da Índia centers on:',
      ['Whether networks constitute colonialism', 'The profitability of individual forts', 'Whether Portugal had written colonial policy', 'The number of Portuguese settlers'],
      0,
      'Thomaz\'s trade-network model downplays systematic coercion; postcolonial scholars argue the cartaz system, enslavement, and forced labor constitute colonialism.',
    ],
    [
      'The structural roots of the 1640 Restoration lay in:',
      ['Fiscal integration, tinta tax', 'Pure hatred of Spanish rule', 'A unified popular uprising', 'The Spanish king\'s personal tyranny'],
      0,
      'Spanish fiscal centralization under Olivares increased direct levies. The tinta tax on foodstuffs threatened merchant profit margins, making fiscal autonomy the material basis for rebellion.',
    ],
    [
      'Regarding the Miguelite Wars, modern scholarship emphasizes that the liberal victory was:',
      ['Contingent, not inevitable', 'Inevitable due to liberalism\'s superiority', 'Irrelevant to Portuguese history', 'Decided entirely by peasant action'],
      0,
      'The absolutist faction retained regional strength; liberal triumph depended on British intervention (1831–1834) and the inability to reconstruct a unified absolutist coalition.',
    ],
    [
      'The First Republic\'s fiscal crisis (deficits by 1912) stemmed from:',
      ['Contradictory fiscal imperatives', 'Mismanagement alone', 'External monarchist sabotage', 'Excessive military spending on colonial wars'],
      0,
      'Liberal reforms alienated agrarian landowners; tariff cuts lost revenue. The state could not simultaneously spend, reform institutions, and collect taxes.',
    ],
    [
      'Salazar\'s philosophical debt to integralist theorist Mário de Sacadura Cabral manifested in:',
      ['Catholic corporatism', 'Adopting Nazi racial theory', 'Commitment to democracy', 'No systematic philosophy'],
      0,
      'Salazar grounded corporatism in scholastic natural law and medieval guild models. Unlike fascism, it lacked revolutionary dynamism and racial biologism.',
    ],
    [
      'Portugal\'s WWII "non-belligerence" concealed:',
      ['Quiet hedging by both sides', 'Complete neutrality', 'Commitment to the Allies', 'Ignorance of Nazi activities'],
      0,
      'Gestapo agents operated unimpeded in Lisbon; the government tolerated British intelligence while selling wolfram to Germany. "Neutrality" was performative hedging.',
    ],
    [
      'The PIDE-DGS secret police\'s structural effectiveness derived from:',
      ['Middle-class informant networks', 'Superior surveillance technology', 'Mass imprisonment programs', 'Military dominance and patrols'],
      0,
      'PIDE lacked centralized archives or technical sophistication; it relied on informant networks. This created suspicion but left it vulnerable when military support dissolved.',
    ],
    [
      'After 1964, the Colonial Wars escalated in military significance because:',
      ['The war looked unwinnable', 'Portugal won decisively', 'Allied intervention succeeded', 'A peace agreement was negotiated'],
      0,
      'By 1970–1974, insurgents deployed conventional tactics; Portuguese casualties spiked. This military stalemate convinced the officer corps the regime was bankrupt.',
    ],
    [
      'The MFA\'s internal ideological heterogeneity (communists, socialists, liberals) was stabilized toward democracy through:',
      ['The Socialist Party\'s 1976 win', 'A Communist Party takeover', 'French military intervention', 'Ideological consensus on the left'],
      0,
      'The Socialist Party won the 1976 election; the CP lacked unified leadership within the MFA. Western pressure (EC accession) pushed the military toward constitutional democracy.',
    ],
    [
      'Lusotropicalism—Salazar\'s ideological justification of Portuguese colonialism via Freyre\'s work—claimed:',
      ['A myth of benign racial mixture', 'Colonies received equal treatment', 'Race played no role in Portuguese colonialism', 'The theory was scientifically proven'],
      0,
      'Freyre argued Portuguese colonizers were racially and culturally flexible, producing harmonious miscegenation. Postcolonial scholarship shows this masked brutal racial stratification.',
    ],
    [
      'Why did Portugal immediately decolonize (within weeks) after the Carnation Revolution?',
      ['Radicals rushed decrees to block moderates', 'The colonies unanimously demanded independence', 'Colonial wars had ended', 'International law required it'],
      0,
      'Radical MFA president Gonçalves moved quickly on decolonization to prevent conservative officers from reasserting control. This left power vacuums filled by Cold War factions.',
    ],
    [
      'The 1976 Constitution\'s semi-presidential system reflected:',
      ['An MFA-parliament compromise', 'Pure parliamentary supremacy', 'Military dictatorship formalized', 'Presidentialism without checks'],
      0,
      'The Council of the Revolution granted officers institutional veto power. Constitutional amendments (1982, 1989) gradually shifted toward pure parliamentarism.',
    ],
    [
      'Portuguese EU accession negotiations (1977–1985) faced obstacles from:',
      ['Costly EU rule alignment', 'EU rejection', 'Portuguese refusal', 'Rapid, uncontested integration'],
      0,
      'Harmonizing Portuguese law with EU standards proved administratively heavy while decolonization drained state capacity. Agricultural interests resisted tariff liberalization.',
    ],
    [
      'Recent scholarship (Pina Cabral, Vilar) on Portuguese regional identity argues that nation-building systematized:',
      ['Erasing regional identities', 'Respect for regional autonomy', 'Federal political structures', 'No nation-building occurred'],
      0,
      'Cabral and others demonstrate how Estado Novo centralization and Catholic nationalism erased Moorish, Sephardic, and regional cultural legacies underlying Portuguese heterogeneity.',
    ],
  ],
  professor: [
    [
      'Revisionist historiography on the Treaty of Zamora (1297) emphasizes that it formalized rather than created independence, raising the methodological problem of:',
      ['Dating when statehood begins', 'Whether Zamora was a real treaty', 'The reliability of medieval chronicles', 'The role of military conquest'],
      0,
      'Modern scholars debate whether independence derives from dynastic declaration, institutional maturation, or treaty recognition. Zamora\'s significance lies in formalizing prior institutional divergence.',
    ],
    [
      'The historiographic revision of Portuguese Atlantic expansion from "trade network" to "colonial" framing hinges on archival evidence of:',
      ['Slavery and naval coercion', 'The number of forts built', 'Written colonial charters', 'Settler population statistics'],
      0,
      'Archival evidence of slave labor in Madeira, cartaz enforcement, and indigenous depopulation contradicts trade-network narratives. Portuguese expansion involved extractive, racialized labor regimes.',
    ],
    [
      'The fiscal trigger of the 1640 Restoration (Olivares\' tinta tax increase) reveals that Portuguese elite revolt was:',
      ['Profits, not patriotism', 'Purely about national pride', 'Based on popular demand', 'An intellectual movement'],
      0,
      'The tinta tax threatened merchant profit margins directly. Portuguese historiography romanticizes the Restoration as patriotic; it was fundamentally an elite reaction to fiscal integration.',
    ],
    [
      'Regarding the Miguelite Wars, archival research by Carvalho Monteiro demonstrates that absolutist military capability was undermined by:',
      ['Church property splits', 'British intervention alone', 'Popular demand for liberalism', 'Lack of financial resources'],
      0,
      'Church property seizures alienated clerical absolutists; rural absolutists and urban factions split. These internal divisions, exploited by liberals, proved decisive.',
    ],
    [
      'The First Republic\'s fiscal unraveling from 1912 onward was rooted in the institutional impossibility of:',
      ['Rising costs, falling revenue', 'Maintaining monarchy', 'Controlling colonial territories', 'Preventing military intervention'],
      0,
      'Liberal reform rhetoric alienated landowners who controlled tax collection. The state could not lose revenue sources while increasing expenditures on bureaucratic modernization.',
    ],
    [
      'Salazar\'s corporatist ideology diverged from fascism by rooting legitimacy in:',
      ['Catholic natural law', 'Racial superiority', 'Democratic consent', 'Marxist dialectics'],
      0,
      'Salazar invoked scholasticism and medieval guild organization. Unlike fascism\'s vitalism and racial biologism, Portuguese corporatism emphasized traditional Catholic social order.',
    ],
    [
      'Portugal\'s WWII wolfram export to Germany operated within a geopolitical logic of:',
      ['Hedging, not ideology', 'Support for Nazi ideology', 'Neutrality based on distance', 'Economic necessity alone'],
      0,
      'Salazar profited from both sides: selling wolfram to Germany while tolerating British operations in Lisbon. Formal neutrality masked calculated opportunism.',
    ],
    [
      'The PIDE-DGS secret police\'s vulnerability in 1974 stemmed from:',
      ['Reliance on informants', 'Modernization failures', 'Lack of training', 'Excessive centralization'],
      0,
      'PIDE lacked independent enforcement apparatus; it relied on informant networks. Military withdrawal removed the capacity to sustain the denunciation regime.',
    ],
    [
      'The Colonial Wars\' transition from counterinsurgency to conventional warfare (FRELIMO\'s 1970–74 offensive) signaled to the officer corps that:',
      ['The war looked unwinnable', 'Portugal was winning', 'International mediation would succeed', 'The wars would end immediately'],
      0,
      'Rising casualties, territorial concessions, and FRELIMO\'s conventional capabilities demonstrated the regime could not win. Officers concluded continuing the war threatened institutional stability.',
    ],
    [
      'The MFA\'s leftward shift (1974–76) followed a pattern of revolutionary military radicalization, but the shift toward democracy was consolidated by:',
      ['The Socialists\' 1976 win', 'Communist victory in elections', 'Soviet pressure', 'Socialist ideology'],
      0,
      'The Socialist Party\'s 1976 victory undermined Communist and radical claims. Western incentive structures (NATO, EC accession) made liberal democracy more attractive than alternative authoritarianism.',
    ],
    [
      'Lusotropicalism as deployed by Salazar\'s regime functioned as:',
      ['A mask for coercion', 'Scientific truth', 'A critique of racism', 'An economic theory'],
      0,
      'Freyre\'s lusotropicalism legitimized forced assimilation and racial hierarchies by romanticizing miscegenation. Salazar weaponized it to defend extractive colonialism as culturally benign.',
    ],
    [
      'The immediate decolonization (decree within weeks) under radical MFA president Gonçalves occurred because:',
      ['Radicals moved fast', 'Peaceful negotiation', 'International demand', 'Colonial agreement'],
      0,
      'Gonçalves and radical allies designed fast decolonization to prevent conservative officers from negotiating with colonial elites. Speed was a revolutionary tactic, not consensus.',
    ],
    [
      'The 1976 Constitution\'s Council of the Revolution (military veto over executive) represented:',
      ['Unresolved MFA rifts', 'Permanent military rule', 'Pure civilian supremacy', 'No military influence'],
      0,
      'The Council institutionalized military institutional interests without creating dictatorship. Successive constitutional reforms gradually reduced its powers as democracy consolidated.',
    ],
    [
      'Portuguese EU accession required administrative harmonization particularly in:',
      ['Labor and tariff rules', 'Border control', 'Military procurement', 'National language policy'],
      0,
      'EU acquis communautaire (especially labor and environmental standards) conflicted with Portuguese industrial and agricultural interests. Decolonization limited state capacity for legal harmonization.',
    ],
    [
      'The historiographic debate over "Portuguese identity" (Pina Cabral, Lopes da Silva) emphasizes that national consolidation involved:',
      ['Erasing minority legacies', 'Natural cultural unity', 'Absence of regional diversity', 'Federalist accommodation'],
      0,
      'Scholarship reveals how 19th-20th century nation-building erased Moorish architectural legacies, expelled Sephardic communities, and subordinated regional identities to nationalist narrative.',
    ],
  ],
};
